import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import TimesOneMobiledataRoundedIcon from "@mui/icons-material/TimesOneMobiledataRounded";
import { Box, Slider, Tooltip, Typography } from "@mui/material";

import { usePlayer } from "@/components/player";
import { IconButtonPopover } from "@/primitives";

const PLAYBACK_RATE_MAX = 1.3;
const PLAYBACK_RATE_MENU_HEIGHT = 100;
const PLAYBACK_RATE_MIN = 0.7;
const PLAYBACK_RATE_STEP = 0.1;

const formatPlaybackRate = (rate: number): string | number => {
  const rounded = Math.round(rate * 10) / 10;
  return rounded < 1 ? rounded.toString().slice(1, 3) : rounded;
};

export const PlaybackSpeedButton = () => {
  const { t } = useTranslation();

  const [open, setOpen] = useState<boolean>(false);

  const { player, setPlaybackRate } = usePlayer();

  const handlePlaybackRateChange = useCallback(
    (_event: Event, value: number | number[]) => {
      if (Array.isArray(value)) return;
      setPlaybackRate(value);
    },
    [setPlaybackRate],
  );

  const playbackRateColor = useMemo(() => {
    const rate = player.playbackRate;
    if (!rate) return undefined;
    return rate < 0.95 || rate > 1.05 ? "primary" : undefined;
  }, [player.playbackRate]);

  const displayPlaybackRate = useMemo(
    () => formatPlaybackRate(player.playbackRate || 1),
    [player.playbackRate],
  );

  return (
    <>
      <Tooltip title={t("app.areas.playback.speed")}>
        <IconButtonPopover
          color={playbackRateColor}
          onClick={() => setOpen(true)}
          popover={
            <Slider
              aria-label="Playback Rate Slider"
              max={PLAYBACK_RATE_MAX}
              min={PLAYBACK_RATE_MIN}
              onChange={handlePlaybackRateChange}
              onChangeCommitted={() => setOpen(false)}
              orientation="vertical"
              size="small"
              step={PLAYBACK_RATE_STEP}
              value={player.playbackRate || 1}
              valueLabelDisplay="auto"
              slotProps={{
                input: {
                  style: { writingMode: "vertical-lr", direction: "rtl" },
                },
              }}
              valueLabelFormat={(v) => v}
              sx={{
                height: `${PLAYBACK_RATE_MENU_HEIGHT}px`,
              }}
            />
          }
          popoverProps={{
            anchorOrigin: { vertical: "top", horizontal: "center" },
            onClose: () => setOpen(false),
            open,
            slotProps: {
              paper: { sx: { overflow: "visible", pt: 2.5, pb: 1.5, px: 1 } },
            },
            transformOrigin: { vertical: "bottom", horizontal: "center" },
          }}
        >
          <TimesOneMobiledataRoundedIcon sx={{ visibility: "hidden" }} />
          <Box
            sx={{
              alignItems: "center",
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              left: 0,
              lineHeight: 0,
              position: "absolute",
              right: 0,
              top: 0,
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.66rem !important",
                fontWeight: "800",
                mr: 0.2,
              }}
              variant="caption"
            >
              ✕
            </Typography>
            <Typography
              sx={{ fontSize: "0.77rem", fontWeight: "800", ml: 0.2 }}
              variant="caption"
            >
              {displayPlaybackRate}
            </Typography>
          </Box>
        </IconButtonPopover>
      </Tooltip>
    </>
  );
};
